import { Link } from "@chakra-ui/next-js";
import {
  Badge,
  Box,
  Button,
  Divider,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  type StyleProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import type {
  UtilizationReportDataSource,
  UtilizationReportOrderByColumn,
} from "@src/__generated__/graphql";
import type { UtilizationUserReportDataQuery } from "@src/__generated__/urql-graphql";
import type { UtilizationUserReportModalStore } from "@src/components/modules/reports/utilization/stores/utilization-report-modal";
import {
  Avatar,
  type BodyColumnDef,
  DataTable,
  LoadingOverlay,
  TaskStatusBadge,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { ProgressBar } from "@src/components/ui-kit/NewProgressBar";
import { PeriodPicker } from "@src/components/widgets/PeriodPicker";
import { appStore } from "@src/stores/AppStore";
import {
  currency,
  HUNDRED_PERCENT,
  percentFormatter,
} from "@src/utils/formatters";
import { formatSecondsToHours } from "@src/utils/time";
import type { DateRange } from "@src/utils/types";
import { format } from "date-fns";
import { uniqueId } from "lodash";
import { observer } from "mobx-react-lite";
import { type FC, Fragment, type ReactNode, useMemo } from "react";
import { BillableUtilizationStatCard } from "./stat-cards/BillableUtilization";
import { TotalCapacityStatCard } from "./stat-cards/TotalCapacity";
import { TrackedAllocatedStatCard } from "./stat-cards/TrackedAllocated";
import { UtilizationStatCard } from "./stat-cards/Utilization";

type UtilizationReportRow =
  UtilizationUserReportDataQuery["utilizationTaskReport"][0];
type ModalTableRow = UtilizationReportRow & { id: string };

function createColumns(
  dateRange: DateRange,
): BodyColumnDef<ModalTableRow, UtilizationReportOrderByColumn>[] {
  return [
    {
      id: "project",
      meta: { label: t`Project` },
      header: () => t`Project`,
      cell: ({ row }) =>
        row.original.task.ourWorkBudgetItem ? (
          <Link
            target="_blank"
            rel="noreferrer noopener"
            href={{
              pathname: "/projects/detail",
              query: { id: row.original.task.ourWorkBudgetItem.project.id },
            }}
          >
            {row.original.task.ourWorkBudgetItem.project.code}{" "}
            {row.original.task.ourWorkBudgetItem.project.title}
          </Link>
        ) : (
          <Trans>Unassigned</Trans>
        ),
    },
    {
      id: "budget-item",
      meta: { label: t`Budget item` },
      header: () => t`Budget item`,
      cell: ({ row }) =>
        row.original.task.ourWorkBudgetItem ? (
          <Link
            href={{
              pathname: "/projects/detail",
              query: { id: row.original.task.ourWorkBudgetItem.project.id },
              hash: row.original.task.ourWorkBudgetItem.title
                .replaceAll(" ", "-")
                .toLowerCase(),
            }}
            target="_blank"
            rel="noreferrer noopener"
          >
            {row.original.task.ourWorkBudgetItem.title}
          </Link>
        ) : (
          <Trans>Unassigned</Trans>
        ),
    },
    {
      id: "task",
      meta: { label: t`Task` },
      header: () => t`Task`,
      cell: ({ row }) => (
        <Button
          maxW="300px"
          fontWeight="medium"
          isTruncated
          onClick={() => {
            appStore.taskDetailModalStore.disclosureState.onOpen({
              id: row.original.task.id,
            });
          }}
          role="link"
          size="xs"
          variant="link"
        >
          {row.original.task.name}
        </Button>
      ),
    },
    {
      id: "status",
      meta: { label: t`Status` },
      header: () => t`Status`,
      cell: ({ row }) => (
        <TaskStatusBadge
          rounded="md"
          name={row.original.task.status.name}
          foreground_color={row.original.task.status.foreground_color}
          background_color={row.original.task.status.background_color}
        />
      ),
    },
    {
      id: "stats",
      meta: { label: t`Tracked | Allocated | Budget in task`, align: "right" },
      header: () => t`Tracked | Allocated | Budget in task`,
      cell: ({ row }) => {
        const r = row.original;

        return (
          <Popover isLazy trigger="hover">
            <PopoverTrigger>
              <VStack>
                <HStack
                  align="center"
                  justify="space-between"
                  w="full"
                  h="4"
                  fontSize="13px"
                  fontWeight="medium"
                  divider={<Divider bg="gray.500" orientation="vertical" />}
                >
                  <Text>
                    <Icon w="4" h="4" color="green.700" name="clock" />
                    &nbsp;
                    {formatSecondsToHours(r.tracked_time_in_range ?? 0, 1)}
                    &nbsp;(
                    {formatSecondsToHours(r.tracked_time ?? 0, 1)})
                  </Text>
                  <Text>
                    {formatSecondsToHours(r.allocated_time_in_range ?? 0, 1)}
                    &nbsp;({formatSecondsToHours(r.allocated_time ?? 0, 1)})
                  </Text>
                  <Text>
                    {formatSecondsToHours(r.budgeted_time_in_range ?? 0, 1)}
                    &nbsp;({formatSecondsToHours(r.budgeted_time ?? 0, 1)})
                  </Text>
                </HStack>
                <ProgressBar
                  segments={[
                    {
                      bg: "teal.200",
                      percent: (r.tracked_time_percent ?? 0) * HUNDRED_PERCENT,
                    },
                    {
                      bg: "teal.400",
                      percent:
                        (r.tracked_time_in_range_percent ?? 0) *
                        HUNDRED_PERCENT,
                    },
                    {
                      bg: "#956FD7",
                      percent:
                        (r.allocated_time_percent ?? 0) * HUNDRED_PERCENT,
                    },
                    {
                      bg: "#c4a1ff",
                      percent:
                        (r.allocated_time_in_range_percent ?? 0) *
                        HUNDRED_PERCENT,
                    },
                    {
                      bg: r.over_budget_time ? "red.500" : "gray.200",
                      percent:
                        (r.over_budget_time
                          ? (r.over_budget_time_in_percent ?? 0)
                          : (r.remaining_time_in_percent ?? 0)) *
                        HUNDRED_PERCENT,
                    },
                  ]}
                  size="sm"
                />
              </VStack>
            </PopoverTrigger>
            <Portal>
              <PopoverContent w="fit-content" borderColor="gray.700">
                <PopoverBody
                  p="0"
                  color="whiteAlpha.900"
                  bg="gray.700"
                  borderRadius="4"
                >
                  <VStack align="start" p="4" spacing="4">
                    <StatsTooltipItem
                      time={r.budgeted_time ?? undefined}
                      title={t`Total Task Budget`}
                      amount={r.total_task_budget_amount}
                    />
                    {dateRange.start && (
                      <StatsTooltipItem
                        time={r.budgeted_time_in_range ?? undefined}
                        title={
                          t`Task Budget` +
                          ` (${format(dateRange.start, "MMMM")})`
                        }
                        amount={r.total_task_budget_in_range}
                      />
                    )}
                  </VStack>
                  <Divider orientation="horizontal" variant="dashed" />
                  <VStack align="start" p="4" spacing="4">
                    <HStack minW="340px" spacing="4">
                      <VStack align="start" justify="start">
                        <StatsTooltipItem
                          time={r.tracked_time_in_range ?? undefined}
                          title={t`Tracked (within date range)`}
                          amount={r.tracked_in_range_amount}
                          icon={<Icon name="clock" color="inherit" />}
                          color="teal.400"
                          percent={r.tracked_time_in_range_percent}
                        />
                        <StatsTooltipItem
                          time={r.tracked_time ?? undefined}
                          percent={r.tracked_time_percent}
                          title={t`Tracked (total)`}
                          amount={r.tracked_amount}
                          color="teal.200"
                          icon={<Icon name="clock" color="inherit" />}
                        />
                      </VStack>
                      <VStack align="start" justify="start">
                        <StatsTooltipItem
                          time={r.allocated_time_in_range ?? undefined}
                          title={t`Allocated (within date range)`}
                          amount={r.allocated_in_range_amount}
                          color="#956FD7"
                          percent={r.allocated_time_in_range_percent}
                        />
                        <StatsTooltipItem
                          time={r.allocated_time ?? undefined}
                          percent={r.allocated_time_percent}
                          title={t`Allocated (total)`}
                          amount={r.allocated_amount}
                          color="#C4A1FF"
                        />
                      </VStack>
                    </HStack>
                    {r.over_budget_time ? (
                      <StatsTooltipItem
                        time={r.over_budget_time}
                        amount={r.over_budget_amount}
                        percent={r.over_budget_time_in_percent}
                        color="red.500"
                        title={t`Over Budget`}
                      />
                    ) : (
                      <StatsTooltipItem
                        time={r.remaining_time}
                        amount={r.remaining_amount}
                        percent={r.remaining_time_in_percent}
                        title={t`Left to allocate`}
                      />
                    )}
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>
        );
      },
    },
  ];
}

const StatsTooltipItem: FC<{
  title: string;
  time: number | undefined;
  amount: number | undefined | null;
  percent?: number | undefined | null;
  icon?: ReactNode;
  color?: StyleProps["bg"];
}> = ({
  title,
  time = 0,
  amount = 0,
  icon = null,
  color = "white",
  percent,
}) => {
  return (
    <VStack align="start" spacing="2">
      <Text>{title}</Text>
      <HStack
        align="center"
        color={color}
        divider={<Divider h="4" bg="gray.200" orientation="vertical" />}
        spacing="2"
      >
        <Text color="inherit" fontWeight="medium">
          {icon}
          {icon && <Fragment>&nbsp;</Fragment>}
          {formatSecondsToHours(time, 1)}
        </Text>
        <Text color="inherit">
          {currency.formatByWorkspace(amount ?? 0)}&nbsp;&nbsp;
          {(!!percent || percent === 0) && (
            <Badge bg={color}>
              {percentFormatter().format(percent * 0.01)}
            </Badge>
          )}
        </Text>
      </HStack>
    </VStack>
  );
};

type UtilizationUserReportModalProps = {
  store: UtilizationUserReportModalStore;
  activeDataSource: UtilizationReportDataSource | undefined;
};

export const UtilizationUserReportModal = observer(
  function UtilizationUserReportModal({
    store,
    activeDataSource,
  }: UtilizationUserReportModalProps) {
    const cols = useMemo(
      () => createColumns(store.dateRange),
      [store.dateRange],
    );
    const report = store.data?.utilizationUserReport?.report;
    const user = store.data?.utilizationUserReport?.user;

    return (
      <Modal
        blockScrollOnMount={false}
        isCentered
        isOpen={store.modalState.isOpen}
        onClose={store.modalState.onClose}
        scrollBehavior="inside"
        size="6xl"
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody pt="10">
            <LoadingOverlay
              isLoading={store.isFetching}
              minH="200px"
              position="absolute"
            >
              <Stack h="full" dir="vertical">
                <HStack justifyContent="space-between">
                  <HStack spacing="2">
                    <Avatar
                      w="56px"
                      h="56px"
                      name={user?.full_name}
                      src={user?.image?.urls.thumbnail}
                      colorScheme={user?.profile?.hex_color}
                    />
                    <Stack>
                      <Heading fontSize="24px">{user?.full_name}</Heading>
                      <Text color="grey.500" fontWeight="medium" size="sm">
                        {user?.profile?.defaultWorktype.title} |{" "}
                        {user?.team?.name}
                      </Text>
                    </Stack>
                  </HStack>
                  <PeriodPicker
                    value={store.dateRange}
                    onChange={(range) => {
                      store.dateRange = range;
                      store.fetchData();
                    }}
                  />
                </HStack>

                <DataTable.Stats px="0" spacing="2" pb="0">
                  <TotalCapacityStatCard
                    totalTime={report?.total_time}
                    availableTime={report?.available_time}
                    timeOff={report?.time_off}
                  />
                  <TrackedAllocatedStatCard
                    allocatedTime={report?.allocated_time ?? 0}
                    trackedTime={report?.tracked_time ?? 0}
                    taskBudgetedTime={report?.budgeted_time ?? 0}
                    totalTime={report?.available_time}
                    combinedTime={report?.combined_time ?? 0}
                    activeDataSource={activeDataSource}
                  />
                  <UtilizationStatCard
                    percent={report?.utilization_percentage ?? 0}
                    totalTime={report?.available_time}
                    combinedTime={report?.combined_time ?? 0}
                    allocatedTime={report?.allocated_time ?? 0}
                    budgetedTime={report?.budgeted_time ?? 0}
                    trackedTime={report?.tracked_time ?? 0}
                    month={store.dateRange.start}
                    activeDataSource={activeDataSource}
                    utilizationRange={{
                      from: user?.profile?.utilization_range_from ?? 80,
                      to: user?.profile?.utilization_range_to ?? 100,
                    }}
                  />
                  <BillableUtilizationStatCard
                    totalTime={report?.available_time}
                    percent={report?.utilization_percentage_billable ?? 0}
                    combinedTime={report?.combined_time ?? 0}
                    combinedTimeBillable={report?.combined_time_billable ?? 0}
                    allocatedTime={report?.allocated_time ?? 0}
                    allocatedTimeBillable={report?.allocated_time_billable ?? 0}
                    budgetedTime={report?.budgeted_time ?? 0}
                    budgetedTimeBillable={report?.budgeted_time_billable ?? 0}
                    trackedTime={report?.tracked_time ?? 0}
                    trackedTimeBillable={report?.tracked_time_billable ?? 0}
                    timeOffTime={report?.time_off}
                    activeDataSource={activeDataSource}
                    utilizationRange={{
                      from: user?.profile?.utilization_range_from ?? 80,
                      to: user?.profile?.utilization_range_to ?? 100,
                    }}
                  />
                </DataTable.Stats>

                <Text my="2" fontSize="lg" fontWeight="bold">
                  <Trans>Active tasks in</Trans>&nbsp;
                  {format(store.dateRange.start, "MMMM yyyy")}
                </Text>

                <Box
                  overflow="hidden"
                  borderWidth="1px"
                  borderColor="grey.100"
                  rounded="md"
                >
                  <DataTable.Body<ModalTableRow, UtilizationReportOrderByColumn>
                    tableKey={store.modalId}
                    enableColumnVisibilityControl={false}
                    data={
                      store.data?.utilizationTaskReport.map((r) => ({
                        ...r,
                        id: uniqueId(),
                      })) ?? []
                    }
                    columns={cols}
                  />
                </Box>
              </Stack>
            </LoadingOverlay>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  },
);
